import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { transactions, contacts } from '../utils/api';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const TransactionEditForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    transaction_date: new Date().toISOString(),
    price: '',
    fees: '',
    contact_id: '',
    notes: ''
  });
  const [contactList, setContactList] = useState([]);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactData, setNewContactData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    contactType: 'buyer'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await transactions.getById(id);
        setFormData({
          ...response,
          transaction_date: response.transaction_date ? new Date(response.transaction_date) : new Date().toISOString(),
          contact_id: response.contact_id || '',
          price: response.price || '',
          fees: response.fees || '',
          notes: response.notes || ''
        });
      } catch (err) {
        setError('Failed to load transaction');
        console.error('Error fetching transaction:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await contacts.getAll();
        setContactList(response || []);
      } catch (err) {
        console.error('Error fetching contacts:', err);
      }
    };

    fetchContacts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedData = {
        ...formData,
        transaction_date: formData.transaction_date.toISOString(),
        price: parseFloat(formData.price),
        fees: parseFloat(formData.fees)
      };
      await transactions.update(id, updatedData);
      navigate('/transactions');
    } catch (err) {
      setError('Failed to update transaction');
      console.error('Error updating transaction:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) return <div className="p-4">Loading transaction...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Edit Transaction</h1>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Transaction Date
          </label>
          <DatePicker
            selected={formData.transaction_date}
            onChange={date => setFormData(prev => ({ ...prev, transaction_date: date }))}
            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Price
          </label>
          <input
            inputmode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            step="0.01"
            name="price"
            value={formData.price}
            onChange={handleChange}
            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fees
          </label>
          <input
            inputmode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            step="0.01"
            name="fees"
            value={formData.fees}
            onChange={handleChange}
            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Sold To
          </label>
          {!showNewContactForm ? (
            <>
              <select
                name="contact_id"
                value={formData.contact_id}
                onChange={handleChange}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              >
                <option value="">Select a contact</option>
                {contactList.map(contact => (
                  <option key={contact.id} value={contact.id}>
                    {contact.company_name || `${contact.first_name} ${contact.last_name}`}
                  </option>
                ))}
              </select>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setShowNewContactForm(true)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add New Contact
                </button>
                <Link
                  to={formData.contact_id ? `/contacts/${formData.contact_id}` : '#'}
                  className={`text-sm ${formData.contact_id ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 cursor-not-allowed'}`}
                  onClick={e => !formData.contact_id && e.preventDefault()}
                >
                  View Contact Details
                </Link>
              </div>
            </>
          ) : (
            <div className="bg-gray-50 p-4 rounded-md border space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={newContactData.firstName}
                  onChange={(e) => setNewContactData(prev => ({
                    ...prev,
                    firstName: e.target.value
                  }))}
                  required
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={newContactData.lastName}
                  onChange={(e) => setNewContactData(prev => ({
                    ...prev,
                    lastName: e.target.value
                  }))}
                  required
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={newContactData.email}
                  onChange={(e) => setNewContactData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={newContactData.phone}
                  onChange={(e) => setNewContactData(prev => ({
                    ...prev,
                    phone: e.target.value
                  }))}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Type
                </label>
                <select
                  name="contactType"
                  value={newContactData.contactType}
                  onChange={(e) => setNewContactData(prev => ({
                    ...prev,
                    contactType: e.target.value
                  }))}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                >
                  <option value="buyer">Buyer</option>
                  <option value="seller">Seller</option>
                  <option value="both">Both</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const response = await contacts.create(newContactData);
                      setContactList(prev => [...prev, response]);
                      setFormData(prev => ({
                        ...prev,
                        contact_id: response.id
                      }));
                      setShowNewContactForm(false);
                    } catch (err) {
                      console.error('Error creating contact:', err);
                    }
                  }}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Save Contact
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewContactForm(false)}
                  className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/transactions')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default TransactionEditForm;
